import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Manifesto", href: "#manifesto" },
  { label: "Craft", href: "#pillars" },
  { label: "Work", href: "#work" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when the mobile menu is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    // small delay so the menu can close before we scroll
    setTimeout(() => {
      const target = href === "body" ? document.body : document.querySelector(href);
      target?.scrollIntoView({ behavior: "smooth" });
    }, 60);
  };

  return (
    <motion.nav
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-[100] transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled || open
          ? "bg-[#050505]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1500px] px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#top"
          onClick={(e) => go(e, "body")}
          data-testid="nav-logo"
          className="font-display text-xl md:text-3xl tracking-wide text-[#f2f0e6] hover:text-[#10b981] transition-colors duration-300"
        >
          PIYUSH SONI<span className="text-[#10b981]">.</span>
        </a>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => go(e, l.href)}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="group relative text-sm font-medium uppercase tracking-[0.18em] text-[#b9b6a8] hover:text-[#f2f0e6] transition-colors duration-300"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#10b981] transition-[width] duration-300 group-hover:w-full" />
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <a
            href="#contact"
            onClick={(e) => go(e, "#contact")}
            data-testid="nav-contact-btn"
            className="group hidden sm:inline-flex items-center gap-2 rounded-full border border-[#10b981]/40 bg-[#10b981]/5 px-4 md:px-5 py-2.5 md:py-2 text-[11px] md:text-sm font-semibold uppercase tracking-[0.14em] text-[#10b981] hover:bg-[#10b981] hover:text-[#04140d] transition-colors duration-300"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#10b981] group-hover:bg-[#04140d] transition-colors duration-300" />
            Get in touch
          </a>

          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-[#f2f0e6] transition-colors duration-300 hover:border-[#10b981] hover:text-[#10b981]"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-testid="mobile-menu"
            className="md:hidden overflow-hidden border-t border-white/10 bg-[#050505]/95 backdrop-blur-xl"
          >
            <div className="flex flex-col px-5 py-6">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  initial={{ y: 12, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.05 + i * 0.06, duration: 0.35 }}
                  className="border-b border-white/5 py-4 text-sm font-semibold uppercase tracking-[0.28em] text-[#f2f0e6] hover:text-[#10b981]"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                href="#contact"
                onClick={(e) => go(e, "#contact")}
                data-testid="mobile-nav-contact"
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.05 + LINKS.length * 0.06, duration: 0.35 }}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#10b981] px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.22em] text-[#04140d]"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#04140d]" />
                Get in touch
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
